import * as apicall from "../components/Apicall";

// Booking-workflow templates: a reusable list of steps that is attached to a
// service category, and copied onto each booking made for it. Reads resolve to
// { status, success, message, data } and throw on a non-2xx; writes go through
// apicall.post, which never throws on a 4xx - callers check `success`
// (a business-rule failure is { success: false, message }).

/* ---------------------------------------------------------------- *
 *  Templates
 * ---------------------------------------------------------------- */
// data.data: this vendor's templates, then the shipped starters (is_starter: true),
// each { id, name, description, steps, category_ids, offering_ids, industry_id, ... }
export const getWorkflowTemplates = () => apicall.get("workflow_templates");
export const getWorkflowTemplate = (id) => apicall.get(`workflow_templates/${id}`);
export const createWorkflowTemplate = (data) => apicall.post(data, "workflow_templates");
export const updateWorkflowTemplate = (id, data) => apicall.post(data, `workflow_templates/${id}`);
export const removeWorkflowTemplate = (id) => apicall.post({}, `workflow_templates/${id}/deactivate`);
// Copies a shipped starter into the vendor's own workflows. data.data: { id, name }
export const cloneStarterWorkflow = (key) => apicall.post({}, `workflow_templates/starters/${encodeURIComponent(key)}/clone`);

/* ---------------------------------------------------------------- *
 *  Which services use which workflow
 * ---------------------------------------------------------------- */
// data.data: [{ scope: 'CATEGORY' | 'OFFERING', scope_id, template_id | null }]
export const getWorkflowLinks = () => apicall.get("workflow_links");
// Makes these categories/offerings use the template (and unlinks any other that used it).
export const setWorkflowTemplateLinks = (id, { categoryIds, offeringIds }) =>
  apicall.post({ category_ids: categoryIds, offering_ids: offeringIds }, `workflow_templates/${id}/links`);
// One link: { scope, scope_id, template_id } (template_id null on an OFFERING = "no workflow"),
// or { scope, scope_id, clear: true } so the offering follows its category again.
export const setWorkflowLink = (link) => apicall.post(link, "workflow_links/set");

// The template (with steps) a booking for this category + first offering gets, or null.
export const resolveWorkflow = ({ serviceId, offeringIds }) => {
  const q = new URLSearchParams();
  if (serviceId) q.set("service_id", serviceId);
  if (offeringIds?.length) q.set("offering_ids", offeringIds.join(","));
  return apicall.get(`workflow_templates/resolve?${q.toString()}`);
};

/* ---------------------------------------------------------------- *
 *  On a booking
 * ---------------------------------------------------------------- */
// The workflow a booking was made with and who is on each step (data: null when it has none).
export const getBookingWorkflow = (groupId) => apicall.get(`booking_workflow?group_id=${encodeURIComponent(groupId)}`);

// Who can fill a step, best first. data: { crew: [{ user_id, name, skill_key, in_pool, busy, busy_with }],
// pool_size, auto_pick }. skill: PROFESSIONAL | DRIVER | ANY.
export const getEligibleCrew = ({ skill, resourceId, date, time, durationMin, excludeGroupId }) => {
  const q = new URLSearchParams();
  if (skill) q.set("skill", skill);
  if (resourceId) q.set("resource_id", resourceId);
  if (date) q.set("date", date);
  if (time) q.set("time", time);
  if (durationMin) q.set("duration_min", durationMin);
  if (excludeGroupId) q.set("exclude_group_id", excludeGroupId);
  return apicall.get(`workflow_eligible_crew?${q.toString()}`);
};

// Starts / finishes / skips / reopens / unlocks one step. action: START | DONE | SKIP | REOPEN | UNLOCK.
// data: { workflow, status_changed?, status_id?, status_warning? }. A step someone else just changed
// answers HTTP 409 with { success: false, code, message } (apicall.post hands that back, it does not throw).
export const changeStepState = ({ groupId, stepKey, action, override }) =>
  apicall.post({ group_id: groupId, step_key: stepKey, action, ...(override ? { override: true } : {}) }, "booking_workflow/step_state");

// Puts a person on (userId) or takes them off (null) one step of a saved booking. data: { workflow, notified }
export const assignStepPerson = ({ groupId, stepKey, userId }) =>
  apicall.post({ group_id: groupId, step_key: stepKey, user_id: userId || null }, "booking_workflow/assign");

// Sets the service provider(s) of a saved booking after the fact (the service step was Optional when
// it was booked). userIds: array of professional ids. data: the booking's workflow detail plus
// { providers, notified }. Refused (HTTP 409 / 400, { success: false, message }) for a cancelled booking.
export const assignProvider = ({ groupId, userIds }) =>
  apicall.post({ group_id: groupId, user_ids: userIds }, "booking_workflow/assign_provider");

// Applies the service's workflow to a booking that was saved without one. data: the workflow, or null.
export const ensureBookingWorkflow = (groupId) => apicall.post({ group_id: groupId }, "booking_workflow/ensure");

// Open steps assigned to a person (crew: always their own; staff: pass userId). data: [task]
export const getMyTasks = (userId) => apicall.get(`my_tasks${userId ? `?user_id=${encodeURIComponent(userId)}` : ""}`);

// data: { enabled } - whether the menu should offer Tasks at all.
export const getWorkflowAccess = () => apicall.get("workflow_access");

// Chooses (or clears) a saved booking's resource.
export const setBookingResource = (groupId, resourceIds) =>
  apicall.post({ group_id: groupId, resource_ids: resourceIds }, "booking_workflow/set_resource");

// The vendor's service categories (data: [{ service_id, service_name }]) and, for one
// category, its service types (data: [{ id, name, is_active }]) - what a workflow is attached to.
export const getServiceCategories = () => apicall.get("get_ezp_service");
export const getCategoryOfferings = (serviceId) => apicall.get(`get_ezp_service_offerings?services=${serviceId}`);

export const resultMessage = (res, fallback = "Something went wrong") => res?.message || res?.error || fallback;

/* ---------------------------------------------------------------- *
 *  The builder's one "Who does it?" choice
 * ---------------------------------------------------------------- */
// A step is either a CREW step (a person with a skill, or anyone) or a RESOURCE
// step. The builder shows that as a single dropdown: "Anyone", one entry per skill
// the vendor's industry offers, or the resource.
export const WHO_ANYONE = "ANY";
export const WHO_RESOURCE = "RESOURCE";

export const whoValue = (step) => (step.kind === "RESOURCE" ? WHO_RESOURCE : step.skill_key || WHO_ANYONE);

export const SKILL_PROFESSIONAL = "PROFESSIONAL";

// A step can be the service provider step only while a professional does it.
export const canBePrimary = (step) => step.kind === "CREW" && step.skill_key === SKILL_PROFESSIONAL;

// Changing who does the step. A step that stops being a professional's can no longer be the
// service provider step (the user is then asked to choose another).
export const applyWho = (step, who) => {
  const next = who === WHO_RESOURCE
    ? { ...step, kind: "RESOURCE", skill_key: null }
    : { ...step, kind: "CREW", skill_key: who === WHO_ANYONE ? null : who };
  return canBePrimary(next) ? next : { ...next, is_primary: 0 };
};

// 'Driver' / 'Coach' / 'Anyone' / the resource's own wording.
export const whoLabel = (step, skills = [], resourceLabel = "Resource") => {
  if (step.kind === "RESOURCE") return resourceLabel;
  if (!step.skill_key) return "Anyone";
  return (skills.find((s) => s.skill_key === step.skill_key) || {}).label || step.skill_key;
};

export const newStep = () => ({ name: "", kind: "CREW", skill_key: null, is_mandatory: 1, is_primary: 0, mode: "SEQUENTIAL" });
// A new workflow starts with its service step: the person who completes the service. It is always
// part of the workflow - it can be renamed, moved and made Required or Optional, never removed.
export const newPrimaryStep = () => ({ ...newStep(), name: "Service", skill_key: SKILL_PROFESSIONAL, is_primary: 1 });

// The service step can never be removed, and a workflow never drops below one step.
export const canRemoveStep = (step, count) => count > 1 && !step.is_primary;
// While a professional does the service step, who does it is fixed (changing it would take the
// step away from the person who completes the service).
export const isWhoLocked = (step) => !!step.is_primary && canBePrimary(step);
export const emptyTemplate = () => ({ name: "", description: "", auto_in_progress: 1, auto_complete: 1, steps: [newPrimaryStep()] });

// Makes the step at `index` the service provider step (and the only one), Required. Only used to
// give an older workflow (saved before the flag existed) its default service step - Optional is
// always the vendor's explicit choice, never something opening a workflow can change.
export const makePrimary = (steps, index) =>
  steps.map((s, i) => (i === index ? { ...s, is_primary: 1, is_mandatory: 1 } : s.is_primary ? { ...s, is_primary: 0 } : s));

// While no step is flagged, the first professional step becomes the service provider step - so
// choosing "professional" on the right step needs no extra click. Otherwise the steps are unchanged.
export const withDefaultPrimary = (steps) => {
  if (steps.some((s) => s.is_primary)) return steps;
  const first = steps.findIndex(canBePrimary);
  return first === -1 ? steps : makePrimary(steps, first);
};

// Moves the step at `index` up (-1) or down (+1); out-of-range moves change nothing.
export const moveStep = (steps, index, delta) => {
  const to = index + delta;
  if (to < 0 || to >= steps.length) return steps;
  const next = steps.slice();
  [next[index], next[to]] = [next[to], next[index]];
  return next;
};

// What the server is sent: only the fields it reads, with step_key kept so a booking's
// assignee can be carried across an edit.
export const toPayload = (draft) => ({
  name: String(draft.name || "").trim(),
  description: String(draft.description || "").trim(),
  auto_in_progress: draft.auto_in_progress ? 1 : 0,
  auto_complete: draft.auto_complete ? 1 : 0,
  steps: (draft.steps || []).map((s) => ({
    ...(s.step_key ? { step_key: s.step_key } : {}),
    name: String(s.name || "").trim(),
    kind: s.kind,
    skill_key: s.kind === "RESOURCE" ? null : s.skill_key || null,
    // the service step may be Required or Optional (the provider is assigned after booking)
    is_mandatory: s.is_mandatory ? 1 : 0,
    // always sent, even as 0: it tells the server this client knows about the service provider step
    is_primary: s.is_primary ? 1 : 0,
    mode: s.mode,
  })),
});

// Client-side check before a save, mirroring the server's basic rules so the user
// hears about it immediately. Returns a message, or "" when fine.
export const validateDraft = (draft) => {
  if (!String(draft.name || "").trim()) return "Give the workflow a name";
  const steps = draft.steps || [];
  if (steps.length === 0) return "Add at least one step";
  const blank = steps.findIndex((s) => !String(s.name || "").trim());
  if (blank !== -1) return `Step ${blank + 1} needs a name`;
  if (steps.filter((s) => s.kind === "RESOURCE").length > 1) return "A workflow can have only one resource step";
  const primaries = steps.filter((s) => s.is_primary);
  if (primaries.length === 0) {
    return steps.some(canBePrimary)
      ? "Choose the service provider step: the one that completes the service"
      : "Add a step done by the service provider: the person who completes the service";
  }
  if (primaries.length > 1) return "Only one step can be the service provider step";
  if (!canBePrimary(primaries[0])) return "The service provider step must be done by the service provider";
  return "";
};
